import { useQuery, useMutation } from '@apollo/client';
import {
  profileCurrentUserQuery,
  attachPartnerAccountMutation,
  attachPartnerAccountMutationVariables,
  PartnerId,
  profileCurrentUserQuery_currentUser_partnerAccounts,
  detachPartnerAccountMutation,
  profilePageQuery,
  detachPartnerAccountMutationVariables
} from '../../core-types';
import {
  PROFILE_CURRENT_USER_QUERY,
  ATTACH_PARTNER_ACCOUNT_MUTATION,
  DETACH_PARTNER_ACCOUNT_MUTATION,
  PROFILE_PAGE_QUERY
} from '../Profile/graphql';
import {
  Box,
  Flex,
  Loading,
  Button,
  Alert,
  Text,
  Image,
  LoadableButton,
  Table,
  Caption,
  Tr,
  Th,
  Td
} from '../../components';
import { PartnerAtmDialogForm } from './PartnersForm';
import { FormattedMessage, useIntl } from 'react-intl';
import { globalMessages } from '../../lib/messages';
import { Warning } from '@urbaninfrastructure/react-icons';
import { messages } from './messages';
import { AtmCardPreview } from './AtmCardPreview';
import { useCurrentUser } from '../../lib/core/useCurrentUser';
import { useToasts } from '../../components/Toasts';

function AttachAtmAccount() {
  const intl = useIntl();
  const { addToast } = useToasts();
  const [attachPartnerAccount, { data, loading, error }] = useMutation<
    attachPartnerAccountMutation,
    attachPartnerAccountMutationVariables
  >(ATTACH_PARTNER_ACCOUNT_MUTATION, {
    update: (cache, { data }) => {
      if (!data) {
        return;
      }
      const currentUserResult = cache.readQuery<profileCurrentUserQuery>({
        query: PROFILE_CURRENT_USER_QUERY
      });
      if (
        !currentUserResult?.currentUser ||
        data.attachPartnerAccount.__typename !== 'MilanATMCard'
      ) {
        return;
      }
      const partnerAccounts = currentUserResult.currentUser.partnerAccounts
        ? currentUserResult.currentUser.partnerAccounts
        : [];
      cache.writeQuery<profileCurrentUserQuery>({
        query: PROFILE_CURRENT_USER_QUERY,
        data: {
          ...currentUserResult,
          currentUser: {
            ...currentUserResult.currentUser,
            partnerAccounts: [...partnerAccounts, data.attachPartnerAccount]
          }
        }
      });
    }
  });

  const addMessage = <FormattedMessage {...messages.publicTransportCard} />;
  const attachPartnerDiscountError =
    data &&
    data.attachPartnerAccount.__typename === 'AttachPartnerAccountError' &&
    data.attachPartnerAccount;
  return (
    <PartnerAtmDialogForm
      heading={addMessage}
      renderOpenButton={openPortal => {
        return (
          <Button
            type="button"
            onClick={openPortal}
            size="small"
            variant="primary"
          >
            {addMessage}
          </Button>
        );
      }}
      loading={loading}
      error={
        error || attachPartnerDiscountError ? (
          <>
            {error && (
              <Alert variant="error">
                <FormattedMessage {...globalMessages.genericError} />
              </Alert>
            )}
            {attachPartnerDiscountError && (
              <Alert variant="error">
                {attachPartnerDiscountError.message || (
                  <FormattedMessage {...globalMessages.genericError} />
                )}
              </Alert>
            )}
          </>
        ) : null
      }
      onSubmit={(values, closePortal) => {
        attachPartnerAccount({
          variables: {
            partnerId: PartnerId.milan_atm,
            milanATMInput: {
              serialNumber: values.serialNumber,
              fiscalCode: values.fiscalCode,
              identificationNumber: parseInt(values.identificationNumber)
            }
          }
        }).then(({ data }) => {
          if (data) {
            switch (data.attachPartnerAccount.__typename) {
              case 'MilanATMCard': {
                closePortal();
                addToast({
                  text: intl.formatMessage(messages.milan_atm_card_added),
                  type: 'success'
                });
                break;
              }
              case 'AttachPartnerAccountError': {
                // Expected error handled in UI
                break;
              }
            }
          }
        });
      }}
    />
  );
}

function AtmAccount({
  partnerId,
  atmAccount
}: {
  partnerId: PartnerId;
  atmAccount: profileCurrentUserQuery_currentUser_partnerAccounts;
}) {
  const intl = useIntl();
  const { addToast } = useToasts();
  const [detachPartnerAccount, { loading, error, data }] = useMutation<
    detachPartnerAccountMutation,
    detachPartnerAccountMutationVariables
  >(DETACH_PARTNER_ACCOUNT_MUTATION, {
    onCompleted: data => {
      if (
        data &&
        data.detachPartnerAccount.__typename === 'PartnerAccountDetached'
      ) {
        addToast({
          text: intl.formatMessage(messages.milan_atm_card_removed),
          type: 'success'
        });
      }
    },
    update: (cache, { data }) => {
      if (!data) {
        return;
      }
      const currentUserResult = cache.readQuery<profileCurrentUserQuery>({
        query: PROFILE_CURRENT_USER_QUERY
      });
      if (!currentUserResult?.currentUser) {
        return;
      }

      const { detachPartnerAccount } = data;

      if (detachPartnerAccount.__typename === 'PartnerAccountDetached') {
        const partnerAccounts = currentUserResult.currentUser.partnerAccounts
          ? currentUserResult.currentUser.partnerAccounts
          : [];
        cache.writeQuery<profileCurrentUserQuery>({
          query: PROFILE_CURRENT_USER_QUERY,
          data: {
            ...currentUserResult,
            currentUser: {
              ...currentUserResult.currentUser,
              partnerAccounts: [...partnerAccounts].filter(
                p => p.partnerId !== detachPartnerAccount.partnerId
              )
            }
          }
        });
      }
    }
  });

  const detachPartnerDiscountError =
    data &&
    data.detachPartnerAccount.__typename === 'DetachPartnerAccountError' &&
    data.detachPartnerAccount;

  return (
    <>
      {error && (
        <Alert variant="error">
          <FormattedMessage {...globalMessages.genericError} />
        </Alert>
      )}
      {detachPartnerDiscountError && (
        <Alert variant="error">
          {detachPartnerDiscountError.message || (
            <FormattedMessage {...globalMessages.genericError} />
          )}
        </Alert>
      )}
      <Box mb="xs">
        <Table variant="simple" small>
          <Caption>
            <Flex alignItems="center">
              <Box mr={2} flex={1}>
                <FormattedMessage
                  id="milan_atm_card_title"
                  defaultMessage="ATM card"
                />
              </Box>
              <AtmCardPreview width={30} />
            </Flex>
          </Caption>
          <tbody>
            <Tr>
              <Th scope="row">
                <FormattedMessage {...messages.identificationNumber} />
              </Th>
              <Td textAlign="right">{atmAccount.identificationNumber}</Td>
            </Tr>
            <Tr>
              <Th scope="row">
                <FormattedMessage {...messages.serialNumber} />
              </Th>
              <Td textAlign="right">{atmAccount.serialNumber}</Td>
            </Tr>
          </tbody>
        </Table>

        {!atmAccount.isValid && (
          <Flex mt={2} alignItems="center" color="state.error">
            <Warning size={16} mr={1} />{' '}
            <Text typoStyle="xxs">
              <FormattedMessage {...messages.atmCardNotValid} />
            </Text>
          </Flex>
        )}
      </Box>
      <LoadableButton
        shape="cool"
        variant="primary"
        size="small"
        loading={loading}
        responseState={
          error || detachPartnerDiscountError ? 'error' : 'success'
        }
        onClick={() => {
          if (confirm(intl.formatMessage(messages.milan_atm_confirm_remove))) {
            detachPartnerAccount({ variables: { partnerId } });
          }
        }}
      >
        <FormattedMessage id="remove" defaultMessage="Remove" />
      </LoadableButton>
    </>
  );
}

function AtmPartner({
  partnerAccounts,
  canAddAtmCard
}: {
  partnerAccounts: profileCurrentUserQuery_currentUser_partnerAccounts[];
  canAddAtmCard: boolean;
}) {
  const { currentUser } = useCurrentUser();

  const atmAccounts = partnerAccounts.filter(
    partnerAccount => partnerAccount.__typename === 'MilanATMCard'
  );
  const atmAccount = atmAccounts[0];
  if (!atmAccount && !canAddAtmCard) {
    return null;
  }
  return (
    <Flex flexDirection="column" alignItems="center" maxWidth={600}>
      <Box mb="xs">
        <Image
          src="/static/assets/atm_logo.svg"
          alt="Azienda Trasporti Milanesi"
          width={200}
        />
      </Box>
      <Box>
        {atmAccount &&
          !canAddAtmCard &&
          currentUser &&
          currentUser.currentSubscription &&
          currentUser.currentSubscription.product && (
            <Text as="p">
              You have an ATM card added, but your current subscription (
              {currentUser.currentSubscription.product.name}) does not allow
              unlocks with ATM card
            </Text>
          )}
      </Box>
      {!atmAccount && (
        <Box mx="auto">
          <AttachAtmAccount />
        </Box>
      )}
      {atmAccount && (
        <AtmAccount partnerId={PartnerId.milan_atm} atmAccount={atmAccount} />
      )}
    </Flex>
  );
}

export function SystemPartners() {
  const profilePageResult = useQuery<profilePageQuery>(PROFILE_PAGE_QUERY);
  const { data, loading } = useQuery<profileCurrentUserQuery>(
    PROFILE_CURRENT_USER_QUERY,
    { ssr: false }
  );
  if (!data || loading || profilePageResult.loading) {
    return <Loading justifyContent="center"></Loading>;
  }

  if (!data.currentUser) {
    return null;
  }

  const systemPartners =
    profilePageResult.data && profilePageResult.data.systemPartners
      ? profilePageResult.data.systemPartners.partners
      : [];

  const canAddAtmCard = data.currentUser.canAddAtmCard;
  const partnerAccounts = data.currentUser.partnerAccounts || [];

  return (
    <>
      {systemPartners.map(systemPartner => {
        switch (systemPartner.id) {
          case PartnerId.milan_atm: {
            return (
              <AtmPartner
                key={systemPartner.id}
                partnerAccounts={partnerAccounts}
                canAddAtmCard={canAddAtmCard}
              />
            );
          }
          default: {
            return null;
          }
        }
      })}
    </>
  );
}
